import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useCallOffReplacementGates } from "../../../hooks/useCallOffReplacementGate";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { shouldBypassCallOffDialog } from "../../../utils/utilFunctions";
import { usePayrollConfirmation } from "../../hooks/usePayrollConfirmation";
import type { CallOffChoiceDialogProps } from "./CallOffChoiceDialog";
import type { RemoveStaffRow } from "./removeStaffRows";
import { planRemoval, type RemovalEntry, resolveRemovalEntries } from "./removeStaffSubmit";
import { useRemovalExecution } from "./useRemovalExecution";
import type { RemoveStaffDetails } from "./useRemoveStaffDetails";

type Args = {
	selectedRows: readonly RemoveStaffRow[];
	weekIndex: number;
	dayNo: number;
	staffingDate: DateTime;
	showActual: boolean;
	details: RemoveStaffDetails;
	source: string;
	/** Keeps only these shifts picked — the ones a partly failed removal couldn't remove. */
	replaceSelection: (shiftIds: string[]) => void;
	onClose: (success?: boolean) => void;
};

/** A removal waiting on the "find a replacement?" choice: every pick, and those inside the call-off window. */
type PendingChoice = { entries: RemovalEntry[]; inWindow: RemovalEntry[] };

/**
 * Whether a shift is removed as a call-off (with the replacement choice): the call-off agent is on, this isn't a
 * draft, and the shift starts inside the department's admin call-off window. Everything else is a plain removal.
 */
const useIsInCallOffWindow = () => {
	const { chosenDepartment, timezone } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	return useCallback(
		(startTime: DateTime) => isCallOffEnabled && !isDraftMode && shouldBypassCallOffDialog(startTime, chosenDepartment, timezone),
		[isCallOffEnabled, isDraftMode, chosenDepartment, timezone],
	);
};

/** The replacement gates of the picks that will be offered a call-off — read while picking, so the choice is ready. */
const usePickGates = (selectedRows: readonly RemoveStaffRow[], weekIndex: number, dayNo: number) => {
	const { t } = useTranslation();
	const { hospitalData, chosenDepartment, showErrorSnackbar } = useHospitalData();
	const isInWindow = useIsInCallOffWindow();
	const inWindowShiftIds = useMemo(() => {
		const { entries } = resolveRemovalEntries(selectedRows, hospitalData.weeks[weekIndex], dayNo);
		return entries.filter((entry) => isInWindow(entry.ref.shift.startTime)).map((entry) => entry.row.shiftId);
	}, [selectedRows, hospitalData.weeks, weekIndex, dayNo, isInWindow]);
	const gates = useCallOffReplacementGates({ departmentId: chosenDepartment.department_id, shiftIds: inWindowShiftIds, enabled: inWindowShiftIds.length > 0 });
	const errorKey = gates.isError ? inWindowShiftIds.join(",") : "";
	useEffect(() => {
		if (errorKey) showErrorSnackbar(t("error.general"), { shift_ids: errorKey }, gates.error);
	}, [errorKey, gates.error, showErrorSnackbar, t]);
	return { gates, isInWindow };
};

type ChoiceArgs = {
	pendingChoice: PendingChoice | null;
	gates: CallOffChoiceDialogProps["gates"];
	isRunning: boolean;
	run: (choice: PendingChoice, searchForReplacement: boolean) => Promise<void>;
	close: () => void;
};

/** The "find a replacement?" dialog's props for the removal waiting on it (closed when none is). */
const buildChoiceDialog = ({ pendingChoice, gates, isRunning, run, close }: ChoiceArgs): CallOffChoiceDialogProps => ({
	open: pendingChoice !== null,
	inWindowNames: pendingChoice ? pendingChoice.inWindow.map((entry) => entry.name) : [],
	outsideWindowCount: pendingChoice ? pendingChoice.entries.length - pendingChoice.inWindow.length : 0,
	gates,
	submitting: isRunning,
	onSearch: () => pendingChoice && void run(pendingChoice, true),
	onRemoveOnly: () => pendingChoice && void run(pendingChoice, false),
	// Back is ignored mid-removal: the shifts are already on their way off.
	onCancel: () => !isRunning && close(),
});

/** The worked-hours confirmation: confirming carries the removal on; cancelling drops it. */
const buildPayrollDialog = (entries: RemovalEntry[] | null, proceed: (entries: RemovalEntry[]) => void, close: () => void) => ({
	open: entries !== null,
	onCancel: close,
	onConfirm: () => {
		if (entries) proceed(entries);
		close();
	},
});

/** A pick list as the snackbar / analytics context names it. */
const shiftIdsOf = (entries: readonly RemovalEntry[]): string => entries.map((entry) => entry.row.shiftId).join(",");

/**
 * Carries out one planned removal and lands its outcome: all removed → the panel closes; some failed → only
 * those stay picked. A throw nobody expected (the execution reports its own failures) is surfaced — a snackbar
 * and a tracked error — rather than left as an unhandled rejection behind a `void run(...)`.
 */
const useRunRemoval = (args: Args, clearChoice: () => void) => {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const { trackError } = useAnalytics();
	const execute = useRemovalExecution({ weekIndex: args.weekIndex, dayNo: args.dayNo, staffingDate: args.staffingDate, source: args.source });
	const [isRunning, setIsRunning] = useState(false);
	const { details, onClose, replaceSelection } = args;

	const run = useCallback(
		async ({ entries, inWindow }: PendingChoice, searchForReplacement: boolean) => {
			setIsRunning(true);
			try {
				const result = await execute(planRemoval(entries, inWindow, searchForReplacement), details.toRemovalDetails(), inWindow.length);
				clearChoice();
				if (result.failed.length === 0) onClose(true);
				else replaceSelection(result.failed.map((entry) => entry.row.shiftId));
			} catch (error) {
				clearChoice();
				const message = error instanceof Error ? error.message : String(error);
				// `args.source`, not the panel constant: the same panel is opened from the recommendation dialog
				// too, and every other event on this path already reports the caller. A hardcoded source here files
				// those failures under the wrong entry point, which is the one field you would group by.
				trackError("remove_staff_submit_error", { source: args.source, selected_count: entries.length, error_message: message });
				showErrorSnackbar(t("error.general"), { shift_ids: shiftIdsOf(entries) }, error);
			} finally {
				setIsRunning(false);
			}
		},
		[execute, details, onClose, replaceSelection, clearChoice, trackError, showErrorSnackbar, t],
	);
	return { run, isRunning };
};

type RequestArgs = {
	args: Args;
	isRunning: boolean;
	/** The worked-hours confirmation is needed: park the picks on it instead of carrying on. */
	needsPayrollConfirmation: boolean;
	askPayroll: (entries: RemovalEntry[]) => void;
	afterPayroll: (entries: RemovalEntry[]) => void;
};

/** The button's press: the reason check, the picks resolved against the loaded week, then payroll or onward. */
const useRequestRemove = ({ args, isRunning, needsPayrollConfirmation, askPayroll, afterPayroll }: RequestArgs) => {
	const { t } = useTranslation();
	const { hospitalData, showErrorSnackbar } = useHospitalData();
	const { selectedRows, details, weekIndex, dayNo } = args;
	return useCallback(async () => {
		if (selectedRows.length === 0 || isRunning) return;
		if (!(await details.confirmReason())) return;
		const { entries, missing } = resolveRemovalEntries(selectedRows, hospitalData.weeks[weekIndex], dayNo);
		if (missing.length > 0) {
			showErrorSnackbar(t("remove.staff.error.not.loaded"), { shift_ids: missing.map((row) => row.shiftId).join(",") });
			return;
		}
		if (needsPayrollConfirmation) askPayroll(entries);
		else afterPayroll(entries);
	}, [selectedRows, details, weekIndex, dayNo, isRunning, hospitalData.weeks, showErrorSnackbar, t, needsPayrollConfirmation, askPayroll, afterPayroll]);
};

/**
 * The Remove Staff confirm flow: the reason check → the payroll confirmation (worked hours in an integration-synced
 * department, asked once whatever follows) → one "find a replacement?" choice for any picks inside the call-off
 * window → the removal (one publish, then the call-offs) → one snackbar. On success the panel closes; on a partial
 * failure it stays open with only the failed picks still ticked.
 */
export const useRemoveStaffSubmit = (args: Args) => {
	const needsPayrollConfirmation = usePayrollConfirmation(args.showActual);
	const { gates, isInWindow } = usePickGates(args.selectedRows, args.weekIndex, args.dayNo);
	const [payrollEntries, setPayrollEntries] = useState<RemovalEntry[] | null>(null);
	const [pendingChoice, setPendingChoice] = useState<PendingChoice | null>(null);
	const clearChoice = useCallback(() => setPendingChoice(null), []);
	const { run, isRunning } = useRunRemoval(args, clearChoice);

	const afterPayroll = useCallback(
		(entries: RemovalEntry[]) => {
			const inWindow = entries.filter((entry) => isInWindow(entry.ref.shift.startTime));
			if (inWindow.length > 0) setPendingChoice({ entries, inWindow });
			else void run({ entries, inWindow }, false);
		},
		[isInWindow, run],
	);
	const requestRemove = useRequestRemove({ args, isRunning, needsPayrollConfirmation, askPayroll: setPayrollEntries, afterPayroll });

	const choiceDialog = buildChoiceDialog({ pendingChoice, gates, isRunning, run, close: clearChoice });
	const payrollDialog = buildPayrollDialog(payrollEntries, afterPayroll, () => setPayrollEntries(null));
	return { requestRemove, isSubmitting: isRunning, choiceDialog, payrollDialog };
};

export type RemoveStaffSubmit = ReturnType<typeof useRemoveStaffSubmit>;
